/**
 * Organisation console data hooks (CUJ-84).
 *
 * TanStack Query layer over `orgApi`. Two rules drive every hook here:
 *
 *  1. **Server-authoritative mutations.** Every write in this console is an
 *     ACL / permission / org-structure change — the clinical-safety carve-out
 *     in `frontend/CLAUDE.md`. We do NOT do optimistic updates: showing a grant,
 *     an assignment, a move, or a patient placement as applied before the server
 *     confirms it would let an admin trust a permission the backend rejected.
 *     Each mutation calls the API, then invalidates the affected queries to
 *     re-read committed state.
 *
 *  2. **One source for the tree.** The whole location hierarchy is fetched once
 *     as a flat list (`locationKeys.list`) and folded into a tree in the view.
 *     Per-node detail (`locationKeys.detail`) is only for the breadcrumb path.
 */

import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useAuthStore } from '@/stores/auth';
import { useConfigStore } from '@/stores/config';
import { QUERY_STALE_TIMES } from '@/lib/query-config';
import {
  orgApi,
  type Location,
  type LocationMember,
  type Role,
  type RoleGrant,
  type RoleMember,
} from '@/api/endpoints/org';
import {
  createLocationAccessRequest,
  decideAccessRequest,
  listLocationAccessRequests,
  type AccessRequest,
  type AccessRequestLevel,
} from '@/api/endpoints/accessRequests';
import { ApiError } from '@/api/client';

// --- Query keys ---------------------------------------------------------------

export const orgKeys = {
  all: ['org'] as const,
  locations: () => [...orgKeys.all, 'locations'] as const,
  location: (id: string) => [...orgKeys.all, 'location', id] as const,
  locationMembers: (id: string) => [...orgKeys.all, 'location', id, 'members'] as const,
  locationPatients: (id: string) => [...orgKeys.all, 'location', id, 'patients'] as const,
  locationAccessRequests: (id: string) =>
    [...orgKeys.all, 'location', id, 'access-requests'] as const,
  roles: () => [...orgKeys.all, 'roles'] as const,
  role: (id: string) => [...orgKeys.all, 'role', id] as const,
  roleMembers: (id: string) => [...orgKeys.all, 'role', id, 'members'] as const,
  roleGrants: (id: string) => [...orgKeys.all, 'role', id, 'grants'] as const,
};

// --- Config vocabulary (config-driven, never hardcoded) -----------------------

// A STABLE empty fallback. A zustand selector must never return a fresh
// reference: `(s) => s.x ?? []` allocates a new array every render, which
// zustand's Object.is equality reads as a change → re-render → re-select →
// infinite loop (React "max update depth"). The `?? EMPTY` lives OUTSIDE the
// selector so the selector returns a stable `undefined` when config is absent.
const EMPTY_VOCAB: string[] = [];

/** Allow-list of location kinds the kind picker renders. Empty until /config loads. */
export function useLocationKinds(): string[] {
  return useConfigStore((s) => s.config?.org?.location_kinds) ?? EMPTY_VOCAB;
}

/** FGA object types a role grant may target. Empty until /config loads. */
export function useKnownResourceTypes(): string[] {
  return useConfigStore((s) => s.config?.org?.known_resource_types) ?? EMPTY_VOCAB;
}

/**
 * Onboarding self-assignment guardrail (CUJ-84): `enabled`, the `role_label`
 * marker key, and the self-assignable `location_kinds`. Returns the stable
 * config object reference (or undefined until /config loads, or when the backend
 * omits the block). The Role form reads `role_label` to render the
 * "self-selectable during onboarding" toggle against the SAME key the backend
 * enforces; onboarding reads the whole block to filter the GET /roles and
 * GET /locations vocabulary by the same rules — so neither control ever drifts
 * from enforcement. Returning the nested reference directly (not a fresh object)
 * keeps the selector stable — no React #185 render loop.
 */
export function useSelfAssign():
  | { enabled: boolean; role_label: string; location_kinds: string[] }
  | undefined {
  return useConfigStore((s) => s.config?.org?.self_assign);
}

// --- Location queries ---------------------------------------------------------

/** The complete visible location set (all pages), folded into a tree by the
 *  view. Config-gated: the page walk sizes its pages from the /config-served
 *  org.max_page_size (orgPageSize throws if called before config settles). */
export function useLocations() {
  const isConfigLoaded = useConfigStore((s) => s.isConfigLoaded);
  return useAuthenticatedQuery<Location[]>({
    queryKey: orgKeys.locations(),
    queryFn: () => orgApi.listAllLocations(),
    enabled: isConfigLoaded,
    // Org vocabulary cadence — same config-driven window as the onboarding
    // hook. Mutations refetch via explicit invalidation regardless.
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });
}

/** Ancestor path for the breadcrumb. Children come from the flat list, not here. */
export function useLocationAncestors(id: string | null) {
  return useAuthenticatedQuery({
    queryKey: id ? orgKeys.location(id) : orgKeys.location('none'),
    queryFn: () => orgApi.getLocation(id as string),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

export function useLocationMembers(id: string | null) {
  return useAuthenticatedQuery({
    queryKey: id ? orgKeys.locationMembers(id) : orgKeys.locationMembers('none'),
    queryFn: () => orgApi.listLocationMembers(id as string),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

/** Patients currently admitted to a unit (the read side of admit/discharge). */
export function useLocationPatients(id: string | null) {
  return useAuthenticatedQuery({
    queryKey: id ? orgKeys.locationPatients(id) : orgKeys.locationPatients('none'),
    queryFn: () => orgApi.listLocationPatients(id as string),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

/** Per-location grant state, the shape the members section consumes. */
export interface LocationMembersState {
  members: LocationMember[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Batch-load the DIRECT grants of several locations from ONE place (one query per
 * distinct id, deduped and cached under the SAME `orgKeys.locationMembers` key as
 * `useLocationMembers`). Used to assemble a unit's INHERITED access from its
 * ancestors' grants — the model cascades owner/editor/viewer `from parent`, so a
 * grant on an ancestor confers it on every descendant — without scattering a hook
 * per ancestor. Each per-ancestor query is itself ACL-gated server-side, so an
 * ancestor the caller cannot view simply yields no grants (never a leak). Returns
 * a map keyed by location id; an id absent from a lookup yields `undefined`.
 */
export function useLocationMembersBatch(ids: string[]): Record<string, LocationMembersState> {
  const authReady = useAuthStore((s) => s.authReady);
  const distinctIds = Array.from(new Set(ids));
  const results = useQueries({
    queries: distinctIds.map((id) => ({
      queryKey: orgKeys.locationMembers(id),
      queryFn: () => orgApi.listLocationMembers(id),
      enabled: authReady,
      refetchOnWindowFocus: false,
    })),
  });
  const byId: Record<string, LocationMembersState> = {};
  distinctIds.forEach((id, i) => {
    const r = results[i];
    byId[id] = { members: r.data?.items ?? [], isLoading: r.isLoading, isError: r.isError };
  });
  return byId;
}

// --- Access requests (Story 4) -------------------------------------------------

/**
 * The unit OWNER's pending access requests. The endpoint is owner-gated: a
 * non-owner gets a 403, which is the SECTION-HIDING signal, not an app
 * failure — so 4xx is never retried (OrgUi's `isForbidden` reads it off the
 * error in the section).
 */
export function useLocationAccessRequests(id: string) {
  return useAuthenticatedQuery<AccessRequest[], Error>({
    queryKey: orgKeys.locationAccessRequests(id),
    queryFn: () => listLocationAccessRequests(id),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
      failureCount < 2,
    refetchOnWindowFocus: false,
  });
}

export function useCreateAccessRequest() {
  return useMutation({
    mutationFn: ({
      locationId,
      accessLevel,
      justification,
    }: {
      locationId: string;
      accessLevel: AccessRequestLevel;
      justification?: string;
    }) =>
      createLocationAccessRequest(locationId, {
        access_level: accessLevel,
        ...(justification ? { justification } : {}),
      }),
    // No invalidation: the requester cannot read the owner's queue (403), and
    // the success note renders from the mutation result itself.
  });
}

/**
 * Owner decision. Server-authoritative (clinical-safety carve-out): approve
 * writes the REAL grant before the 200, so on success we refetch both the
 * request queue and the members panel — the grant must appear because the
 * server says it landed, never optimistically.
 */
export function useDecideAccessRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: 'approve' | 'deny';
      locationId: string;
    }) => decideAccessRequest(id, decision),
    onSuccess: (_data, { locationId }) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: orgKeys.locationAccessRequests(locationId) }),
        qc.invalidateQueries({ queryKey: orgKeys.locationMembers(locationId) }),
      ]),
  });
}

// --- Role queries -------------------------------------------------------------

/**
 * The complete role catalogue (all pages — see orgApi.listAllRoles). Resolves
 * only after the full page walk, so callers must render their loading state
 * while `isLoading` (they do — RolesView shows a skeleton). Gated on /config:
 * the roles walk's short-page termination signal compares against the served
 * `org.max_page_size`, so it must not fire while orgPageSize() would still
 * return the pre-config fallback.
 */
export function useRoles() {
  const isConfigLoaded = useConfigStore((s) => s.isConfigLoaded);
  return useAuthenticatedQuery<Role[]>({
    queryKey: orgKeys.roles(),
    queryFn: () => orgApi.listAllRoles(),
    enabled: isConfigLoaded,
    // Same config-driven stale window as useLocations / the onboarding hook.
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });
}

export function useRoleMembers(id: string | null) {
  return useAuthenticatedQuery({
    queryKey: id ? orgKeys.roleMembers(id) : orgKeys.roleMembers('none'),
    queryFn: () => orgApi.listRoleMembers(id as string),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

/** Per-role membership state, the shape a row consumes (kept minimal so a row
 *  stays a pure presentational component). */
export interface RoleMembersState {
  members: RoleMember[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Batch-load the members of several roles from ONE place (one query per DISTINCT
 * role id, deduped and cached by React Query under the same `orgKeys.roleMembers`
 * key as `useRoleMembers`). This lets a list of role grants resolve its members
 * without scattering a `useRoleMembers` hook across N rows — the rows become pure
 * and the fetches are owned by the section. Gated on `authReady` exactly like
 * `useAuthenticatedQuery`. Returns a map keyed by role id; an id absent from a
 * lookup yields `undefined`, so callers supply their own empty fallback.
 */
export function useRoleMembersBatch(ids: string[]): Record<string, RoleMembersState> {
  const authReady = useAuthStore((s) => s.authReady);
  const distinctIds = Array.from(new Set(ids));
  const results = useQueries({
    queries: distinctIds.map((id) => ({
      queryKey: orgKeys.roleMembers(id),
      queryFn: () => orgApi.listRoleMembers(id),
      enabled: authReady,
      refetchOnWindowFocus: false,
    })),
  });
  const byId: Record<string, RoleMembersState> = {};
  distinctIds.forEach((id, i) => {
    const r = results[i];
    byId[id] = { members: r.data?.items ?? [], isLoading: r.isLoading, isError: r.isError };
  });
  return byId;
}

export function useRoleGrants(id: string | null) {
  return useAuthenticatedQuery({
    queryKey: id ? orgKeys.roleGrants(id) : orgKeys.roleGrants('none'),
    queryFn: () => orgApi.listRoleGrants(id as string),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

// --- Location mutations (server-authoritative) --------------------------------

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; kind: string; parent_id?: string; labels?: Record<string, string> }) =>
      orgApi.createLocation(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.locations() }),
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { name?: string; labels?: Record<string, string> } }) =>
      orgApi.updateLocation(id, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: orgKeys.locations() });
      void qc.invalidateQueries({ queryKey: orgKeys.location(id) });
    },
  });
}

export function useMoveLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      orgApi.moveLocation(id, parentId),
    // A move re-points the authz cascade for the whole subtree — invalidate the
    // entire location set, not just the moved node.
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.locations() }),
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => orgApi.deleteLocation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.locations() }),
  });
}

export function useAddLocationMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, member }: { id: string; member: LocationMember }) =>
      orgApi.addLocationMember(id, member),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: orgKeys.locationMembers(id) }),
  });
}

export function useRemoveLocationMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, member }: { id: string; member: LocationMember }) =>
      orgApi.removeLocationMember(id, member),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: orgKeys.locationMembers(id) }),
  });
}

// Admit is also a transfer: the server unlinks the patient's previous unit. The
// client doesn't know that old unit's id, so invalidate EVERY location roster
// (predicate match on the …,'patients' tail) — only currently-active rosters
// actually refetch, so this is cheap and keeps both ends consistent.
//
// It ALSO invalidates the whole-tree list (`orgKeys.locations()`): each unit's
// `direct_patient_count` rides in the location-list response and the console rolls
// it up client-side, so an admit/discharge changes the census of the target unit
// AND every ancestor up to the root. Without this the tree badges and the parent
// SubtreeBreakdown would show a stale count after every admit/discharge until a
// manual refresh.
function invalidateAfterPatientMove(qc: ReturnType<typeof useQueryClient>) {
  // Return BOTH invalidations as one promise so the caller's `onSuccess` (which
  // clears the "removing" spinner and fires `onSettled`) awaits the roster
  // refetch too — not just the location-list one. Otherwise a rapid second
  // admit/discharge could act on a roster that hasn't refetched yet.
  return Promise.all([
    qc.invalidateQueries({
      predicate: (q) =>
        q.queryKey[0] === 'org' && q.queryKey[1] === 'location' && q.queryKey[3] === 'patients',
    }),
    qc.invalidateQueries({ queryKey: orgKeys.locations() }),
  ]);
}

export function useAdmitPatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, patientId }: { locationId: string; patientId: string }) =>
      orgApi.admitPatient(locationId, patientId),
    onSuccess: () => invalidateAfterPatientMove(qc),
  });
}

export function useDischargePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, patientId }: { locationId: string; patientId: string }) =>
      orgApi.dischargePatient(locationId, patientId),
    onSuccess: () => invalidateAfterPatientMove(qc),
  });
}

// --- Role mutations (server-authoritative) ------------------------------------

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; labels?: Record<string, string> }) =>
      orgApi.createRole(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.roles() }),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { name?: string; description?: string; labels?: Record<string, string> } }) =>
      orgApi.updateRole(id, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: orgKeys.roles() });
      void qc.invalidateQueries({ queryKey: orgKeys.role(id) });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => orgApi.deleteRole(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.roles() }),
  });
}

export function useAddRoleMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) => orgApi.addRoleMember(id, userId),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: orgKeys.roleMembers(id) }),
  });
}

export function useRemoveRoleMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) => orgApi.removeRoleMember(id, userId),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: orgKeys.roleMembers(id) }),
  });
}

export function useAddRoleGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, grant }: { id: string; grant: RoleGrant }) => orgApi.addRoleGrant(id, grant),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: orgKeys.roleGrants(id) }),
  });
}

export function useRemoveRoleGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, grant }: { id: string; grant: RoleGrant }) => orgApi.removeRoleGrant(id, grant),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: orgKeys.roleGrants(id) }),
  });
}

/** Map of role id → role for resolving group principals / labels to names. */
export function indexRolesById(roles: Role[] | undefined): Record<string, Role> {
  const m: Record<string, Role> = {};
  for (const r of roles ?? []) m[r.id] = r;
  return m;
}

/**
 * Map of user id → display label ("Name (email)", or name, or email, or the bare
 * id) for resolving grant/membership principals to a human name. One definition
 * for every console view that renders people, so the format never drifts between
 * the location members panel and the role detail. Typed structurally so it does
 * not couple the org layer to the users-endpoint type.
 */
export function indexUsersById(
  users: ReadonlyArray<{ id: string; name?: string | null; email?: string | null }> | undefined,
): Record<string, string> {
  const m: Record<string, string> = {};
  for (const u of users ?? []) {
    m[u.id] = u.name ? (u.email ? `${u.name} (${u.email})` : u.name) : (u.email ?? u.id);
  }
  return m;
}

